import { DebugInformation } from "../debug/DebugInformation";
import React, { ComponentPropsWithoutRef, forwardRef, useState, useImperativeHandle } from "react";
import { DebugLayerObserver } from "@manager/layerObserver/DebugLayerObserver";
import { DebugControls } from "../debug/DebugControls";
import { DebugVariables } from "../debug/DebugVariables";

type DebugComponentProps = ComponentPropsWithoutRef<typeof DebugInformation> & ComponentPropsWithoutRef<typeof DebugVariables>;

type PlaybackInformationProps = Omit<DebugComponentProps, "className" | "style"> & {
  isPlaying: boolean;
};

type Props = {
  className?: string;
  style?: React.CSSProperties;
};

export interface DebugLayerHandle {
  setData(playbackInformation: PlaybackInformationProps): void;

  setObserver(debugLayerObserver: DebugLayerObserver): void;
}

export const DebugLayerComponent = forwardRef<DebugLayerHandle, Props>((props, ref) => {
  const [playbackInformationProps, setPlaybackInformationProps] = useState<PlaybackInformationProps | null>(null);
  const [observer, setObserver] = useState<DebugLayerObserver | null>(null);

  const { className, style } = props;

  // Sadly, for now player is implemented using imperative style of programming
  // maybe in the future I'll be able to create some declarative API
  useImperativeHandle(ref, () => {
    return {
      setData(playbackInformation: PlaybackInformationProps) {
        setPlaybackInformationProps(playbackInformation);
      },

      setObserver(debugLayerObserver: DebugLayerObserver) {
        setObserver(debugLayerObserver);
      },
    };
  });

  if (!playbackInformationProps || !observer) {
    return null;
  }

  return (
    <div className={`c-debugLayer ${className || ""}`}>
      <DebugInformation className={className} style={style} {...playbackInformationProps} />

      <div className="c-debugLayerControls">
        <DebugControls
          isPlaying={playbackInformationProps.isPlaying}
          onPlay={observer.onPlay}
          onPause={observer.onPause}
          onGoToNextFrame={observer.onGoToNextFrame}
          onGoToPreviousFrame={observer.onGoToPreviousFrame}
          onPlaybackSpeedChange={observer.onPlaybackSpeedChange}
        />
      </div>

      <DebugVariables parameters={playbackInformationProps.parameters} />
    </div>
  );
});

DebugLayerComponent.displayName = "DebugLayerComponent";
